import React from 'react';
import { servicesData } from './ServicesData';
import { ChevronRight } from 'lucide-react';

// COLORS — White, Black & Burgundy Premium Palette
const COLORS = {
  bgPrimary: "#ffffff",
  bgSecondary: "#ffffff",
  burgundy: "#6D1A2A",
  burgundyMid: "#9B2335",
  burgundyLight: "#f5eced",
  cardBorder: "#e8e0e0",
  cardBorderHover: "#d0b8bb",
  textDark: "#0D0D0D",
  textMuted: "#666666",
};

const cardStyles = {
  base: {
    position: 'relative',
    backgroundColor: COLORS.bgSecondary,
    border: `1px solid ${COLORS.cardBorder}`,
    borderRadius: '16px',
    padding: '32px 28px 28px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
    transition: 'box-shadow 0.3s, transform 0.3s, border-color 0.3s',
    cursor: 'default',
  },
};

const ServiceCard = ({ service }) => {
  const [hovered, setHovered] = React.useState(false);

  // ✅ Safe: only render icon if it exists on the service object
  const Icon = service.icon ?? null;

  return (
    <div
      style={{
        ...cardStyles.base,
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: hovered
          ? '0 16px 40px rgba(109,26,42,0.10), 0 2px 8px rgba(0,0,0,0.06)'
          : 'none',
        borderColor: hovered ? COLORS.cardBorderHover : COLORS.cardBorder,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top Animated Burgundy Border */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '3px',
          width: hovered ? '100%' : '0%',
          backgroundColor: COLORS.burgundy,
          transition: 'width 0.4s ease',
          borderRadius: '16px 16px 0 0',
        }}
      />

      {/* Content Area */}
      <div>

        {/* ✅ Icon Box — only renders when service.icon is provided */}
        {Icon && (
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: hovered ? COLORS.burgundy : COLORS.burgundyLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              transition: 'background 0.3s, transform 0.3s',
              transform: hovered
                ? 'scale(1.08) rotate(4deg)'
                : 'scale(1) rotate(0deg)',
            }}
          >
            <Icon size={24} color={hovered ? "#fff" : COLORS.burgundy} />
          </div>
        )}

        {/* Title */}
        <h3
          style={{
            fontSize: '20px',
            fontWeight: '600',
            color: hovered ? COLORS.burgundy : COLORS.textDark,
            marginBottom: '10px',
            letterSpacing: '-0.01em',
            transition: 'color 0.2s',
          }}
        >
          {service.title}
        </h3>

        {/* Description — uses shortDesc if available, falls back to intro snippet */}
        <p
          style={{
            fontSize: '14px',
            color: hovered ? '#444444' : COLORS.textMuted,
            lineHeight: '1.65',
            marginBottom: '24px',
            fontWeight: '400',
            transition: 'color 0.2s',
          }}
        >
          {service.shortDesc
            ? service.shortDesc
            : service.fullDesc?.intro
              ? service.fullDesc.intro.slice(0, 120) + '…'
              : ''}
        </p>
      </div>

      {/* Action Link */}
      <a
        href={`/services/${service.id}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '11px',
          fontWeight: '600',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: COLORS.burgundy,
          textDecoration: 'none',
        }}
      >
        <span>Read More</span>
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: `1px solid rgba(109,26,42,${hovered ? '1' : '0.25'})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hovered ? COLORS.burgundy : 'transparent',
            color: hovered ? '#ffffff' : COLORS.burgundy,
            transition: 'all 0.25s',
          }}
        >
          <ChevronRight size={14} />
        </div>
      </a>

      {/* Background Number Detail */}
      <span
        style={{
          position: 'absolute',
          bottom: '-8px',
          right: '-4px',
          fontSize: '72px',
          fontWeight: '900',
          fontStyle: 'italic',
          opacity: 0.04,
          color: COLORS.burgundy,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {service.id}
      </span>
    </div>
  );
};

const ServicesList = () => {
  return (
    <section
      style={{ backgroundColor: COLORS.bgPrimary }}
      className="py-24 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">

        {/* SECTION HEADER */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div
              style={{ backgroundColor: COLORS.burgundy }}
              className="h-[2px] w-14"
            />
            <span
              style={{ color: COLORS.burgundy }}
              className="uppercase tracking-[0.4em] text-[10px] font-bold"
            >
              Our Expertise
            </span>
          </div>
          <h2
            style={{ color: COLORS.textDark }}
            className="text-5xl md:text-7xl font-bold leading-tight tracking-tighter"
          >
            Legal{' '}
            <span style={{ color: '#aaaaaa', fontWeight: 300 }}>
              Practices.
            </span>
          </h2>
        </div>

        {/* THIN DIVIDER */}
        <div
          style={{
            width: '100%',
            height: '1px',
            background: `linear-gradient(90deg, transparent, ${COLORS.cardBorder} 40%, ${COLORS.cardBorder} 60%, transparent)`,
            marginBottom: '48px',
          }}
        />

        {/* SERVICES GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {servicesData.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default ServicesList;